/**
 * DashboardLayoutV2.js
 * 
 * Modern, clean navigation system based purely on permissions.
 * Purple accent theme with sleek, compact design.
 * 
 * Key principles:
 * 1. Universal profile section for ALL users
 * 2. Permission-gated items within profile
 * 3. Admin sections shown based on permissions
 * 4. Superadmin has separate navigation
 * 5. Company Admin sees everything (implicit all permissions)
 * 6. Module (plan) gating respected
 */

import { lazy, Suspense, useState, useEffect, useMemo, useCallback, useRef, startTransition } from 'react';
import { useInDashboardShell } from '@/contexts/DashboardShellContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { Link, useNavigate, useLocation, useOutlet } from 'react-router-dom';
import { 
  LogOut, LayoutDashboard, Settings, Calendar, 
  ChevronLeft, ChevronRight, Users, Briefcase, Clock, FileText, 
  AlertTriangle, Shield, Wallet, User, FileCheck, CheckCircle,
  ClipboardList, CalendarCheck, Building, Menu, X, ChevronDown, ChevronUp,
  MessageSquare, Bell, DollarSign, UserCheck, Home, Package, Tag, Award,
  Calculator, CreditCard, GitBranch, TrendingUp, PenTool, FileSignature,
  Lock, Smartphone, Server, Palette, Mail, CalendarRange, CalendarClock,
  ClipboardCheck, CheckSquare, RefreshCw, Sun, Moon, Folder, Globe, Layers, Link2,
  Brain, Sparkles, Database, HelpCircle, UserX, LayoutTemplate, ShieldCheck,
  Play, BarChart3, Inbox, GraduationCap, Headset, Network
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useToast } from '@/hooks/use-toast';
import { usePermissions } from '@/contexts/PermissionContext';
import { hasSelfServicePermissionsOnly as checkSelfServiceOnly } from '@/utils/selfServicePermissions';
import { useAvailableModules } from '@/hooks/useAvailableModules';
import { useProfileHubAccess } from '@/hooks/useProfileHubAccess';
import { useBranding, BrandLogo, BrandIcon } from '@/contexts/BrandContext';
import ImpersonationBanner from './ImpersonationBanner';
import ComplianceWarningBanner from './ComplianceWarningBanner';
import { ReadOnlyBanner, isReadOnlyMode } from './ReadOnlyModeGate';
import ContextualHelpWidget from './ContextualHelpWidget';
import { Helmet } from 'react-helmet-async';
const CopilotBubbleLazy = lazy(() => import('./copilot/CopilotBubble'));
const CompanySwitcherModalLazy = lazy(() => import('./navigation/CompanySwitcherModal'));
const AlertBellLazy = lazy(() => import('./AlertBell'));
import axios from 'axios';
import { hasPlatformPermission, ownerHintFromUser, pathAllowed, usePlatformAccess } from '@/lib/platformAccess';

const API = process.env.REACT_APP_BACKEND_URL;

// Custom thin scrollbar styles
const scrollbarStyles = `
  .thin-scrollbar::-webkit-scrollbar {
    width: 4px;
  }
  .thin-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .thin-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(155, 155, 155, 0.3);
    border-radius: 4px;
  }
  .thin-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(155, 155, 155, 0.5);
  }
  .thin-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(155, 155, 155, 0.3) transparent;
  }
`;

// ========== SUPERADMIN NAVIGATION ==========
const SUPERADMIN_LINKS = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { 
    section: true, 
    label: 'Platform',
    defaultExpanded: true,
    links: [
      { to: '/companies', icon: Building, label: 'Companies', platformPermission: 'companies' },
      { to: '/audit-firms', icon: ClipboardCheck, label: 'Audit Firms', platformPermission: 'owner' },
      { to: '/plans', icon: Package, label: 'Plans', platformPermission: 'owner' },
      { to: '/modules', icon: Package, label: 'Modules', platformPermission: 'owner' },
      { to: '/features', icon: Layers, label: 'Features', platformPermission: 'owner' },
    ]
  },
  { 
    section: true, 
    label: 'Billing',
    defaultExpanded: false,
    links: [
      { to: '/invoices', icon: DollarSign, label: 'Invoices', platformPermission: 'billing' },
      { to: '/financial-dashboard', icon: TrendingUp, label: 'Financials', platformPermission: 'billing' },
      { to: '/superadmin/credits', icon: Wallet, label: 'Credit Management', platformPermission: 'billing' },
      { to: '/superadmin/xero', icon: Link2, label: 'Xero Integration', platformPermission: 'billing' },
    ]
  },
  { 
    section: true, 
    label: 'Demos',
    defaultExpanded: false,
    links: [
      { to: '/demos/settings', icon: Clock, label: 'My demo hours', platformPermission: 'demo_host' },
      { to: '/demos', icon: Calendar, label: 'Demo Pipeline', platformPermission: ['demo_host', 'demos_all'] },
      { to: '/demos/tours', icon: Play, label: 'Self-Guided Tours', platformPermission: 'demos_all' },
      { to: '/demos/analytics', icon: BarChart3, label: 'Demo Analytics', platformPermission: 'demos_all' },
    ]
  },
  { 
    section: true, 
    label: 'AI',
    defaultExpanded: false,
    links: [
      { to: '/assistant?tab=ai-management', icon: Brain, label: 'AI Management', platformPermission: 'owner' },
      { to: '/assistant?tab=studio', icon: Sparkles, label: 'AI Studio', platformPermission: 'owner' },
    ]
  },
  { 
    section: true, 
    label: 'CRM',
    defaultExpanded: false,
    links: [
      { to: '/superadmin/user-search', icon: Users, label: 'Users', platformPermission: 'owner' },
      { to: '/leads', icon: Users, label: 'Leads', platformPermission: 'owner' },
    ]
  },
  { 
    section: true, 
    label: 'Content',
    defaultExpanded: false,
    links: [
      { to: '/page-builder', icon: LayoutTemplate, label: 'Page Builder', platformPermission: 'page_builder' },
      { to: '/superadmin/landing-pages', icon: BarChart3, label: 'Landing pages', platformPermission: 'landing_pages' },
      { to: '/email-templates', icon: Mail, label: 'Email Templates', platformPermission: 'owner' },
      { to: '/superadmin/help', icon: HelpCircle, label: 'Help Center', platformPermission: 'help_desk' },
      { to: '/superadmin/guided-tours', icon: Sparkles, label: 'Guided Tours', platformPermission: 'owner' },
    ]
  },
  { 
    section: true, 
    label: 'NDIS Config',
    defaultExpanded: false,
    links: [
      { to: '/ndis/pricelists', icon: Tag, label: 'Pricelists', platformPermission: 'owner' },
      { to: '/ndis/award-rules', icon: Award, label: 'Award Rules', platformPermission: 'owner' },
      { to: '/shift-logic', icon: GitBranch, label: 'Shift Logic', platformPermission: 'owner' },
    ]
  },
  { 
    section: true, 
    label: 'System',
    defaultExpanded: false,
    links: [
      { to: '/superadmin/users', icon: ShieldCheck, label: 'Workers', platformPermission: 'owner' },
      { to: '/superadmin/profile', icon: User, label: 'My profile & alerts' },
      { to: '/deployment-manager', icon: Server, label: 'Deployment', platformPermission: 'owner' },
      { to: '/site-settings', icon: Palette, label: 'Site Settings', platformPermission: 'site_settings' },
      { to: '/rbac-health', icon: Shield, label: 'RBAC Health Check', platformPermission: 'owner' },
      { to: '/superadmin/support', icon: Headset, label: 'Support Console', platformPermission: 'help_desk' },
    ]
  },
];

// ========== NAVIGATION CONFIGURATION ==========
// All navigation items with permission and module requirements

const buildCompanyAdminLinks = (modules) => {
  // Module checks are now done via canAccessNavItem using hasModule
  // These variables are no longer needed as items are hidden based on module property
  
  return [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', isPublic: true },
    { to: '/company/help-desk', icon: Headset, label: 'Help Desk', permission: 'help.company_case.manage' },
    { 
      section: true, 
      label: 'Operations',
      defaultExpanded: true,
      links: [
        { to: '/schedule', icon: Calendar, label: 'Schedule', module: 'Scheduling', permission: 'scheduling.shift.view_all' },
        { to: '/timesheets', icon: Clock, label: 'Timesheets', module: 'Payroll', permission: 'timesheet.view_all' },
        { to: '/open-shifts/manage', icon: Globe, label: 'Open Shifts', module: 'Scheduling', permission: 'scheduling.shift.approve' },
        { to: '/approval', icon: CheckCircle, label: 'Approvals', module: 'Scheduling', permission: 'scheduling.approval.view' },
        { to: '/scheduling/on-call', icon: CalendarClock, label: 'On-Call', module: 'OnCall', companyFlag: 'oncallEnabled', permission: 'scheduling.oncall.roster.view' },
        { to: '/scheduling/on-call/inbox', icon: Inbox, label: 'On-Call Inbox', module: 'OnCall', companyFlag: 'oncallEnabled', permission: 'scheduling.oncall.roster.view' },
        { to: '/tasks', icon: CheckSquare, label: 'All Tasks', modules: ['Support Coordination', 'HRM', 'Scheduling', 'Recruitment'], permission: 'tasks.view' },
      ]
    },
    { 
      section: true, 
      label: 'People',
      defaultExpanded: true,
      links: [
        { to: '/participants', icon: Users, label: 'Participants', module: 'CRM', permissions: ['participants.view_all', 'participants.view_team', 'participants.view_own', 'participants.view_assigned'] },
        { to: '/employees', icon: Briefcase, label: 'Employees', modules: ['HRM', 'SC_EmployeeAccess', 'Scheduling'], permission: 'hr.employee.view_all' },
        { to: '/availability/grid', icon: CalendarRange, label: 'Availability', module: 'HRM', permission: 'scheduling.availability.view' },
        { to: '/leave/approve', icon: CheckCircle, label: 'Leave Requests', module: 'HRM', permissions: ['leave.request.view_all', 'leave.request.view_team', 'leave.request.approve'] },
      ]
    },
    { 
      section: true, 
      label: 'NDIS Funding',
      defaultExpanded: false,
      module: 'NDIS',
      links: [
        { to: '/ndis/quotes', icon: FileCheck, label: 'Quotes', module: 'NDIS', permission: 'quotes.view' },
        { to: '/service-agreements-v2', icon: FileText, label: 'Agreements', module: 'NDIS', permission: 'quotes.service_agreement.view' },
        { to: '/ndis/pricelists', icon: Tag, label: 'Pricelist', module: 'NDIS', permission: 'pricelist.service_items.view' },
        { to: '/ndis/templates', icon: FileText, label: 'PDF Settings', module: 'NDIS', permission: 'quotes.view', hideIfModule: 'E-Sign' },
      ]
    },
    {
      section: true,
      label: 'Coordination',
      defaultExpanded: false,
      module: 'Support Coordination',
      links: [
        { to: '/support-coordination', icon: ClipboardList, label: 'SC Dashboard', module: 'Support Coordination', permission: 'sc.dashboard.view' },
        { to: '/tasks?module=support_coordination', icon: CheckSquare, label: 'SC Tasks', module: 'Support Coordination', permission: 'sc.tasks.view' },
        { to: '/support-coordination/case-notes', icon: FileText, label: 'Case Notes', module: 'Support Coordination', permission: 'sc.case_notes.view' },
        { to: '/support-coordination/check-ins', icon: Calendar, label: 'Check-ins', module: 'Support Coordination', permission: 'sc.check_ins.view' },
        { to: '/support-coordination/communication-logs', icon: MessageSquare, label: 'Comm Logs', module: 'Support Coordination', permission: 'sc.communication_logs.view' },
        { to: '/support-coordination/service-providers', icon: UserCheck, label: 'Providers', module: 'Support Coordination', permission: 'sc.service_providers.view' },
        { to: '/support-coordination/reports', icon: TrendingUp, label: 'SC Reports', module: 'Support Coordination', permission: 'sc.reports.view' },
      ]
    },
    {
      section: true,
      label: 'Referrals',
      defaultExpanded: false,
      module: 'Support Coordination',
      links: [
        { to: '/referrals', icon: GitBranch, label: 'Referral Dashboard', module: 'Support Coordination', permission: 'sc.referrals.view' },
      ]
    },
    {
      section: true,
      label: 'Incidents',
      defaultExpanded: false,
      module: 'Incidents',
      links: [
        { to: '/incidents', icon: AlertTriangle, label: 'Incidents', module: 'Incidents', permission: 'incident.report.view_all' },
      ]
    },
    {
      section: true,
      label: 'Registers',
      defaultExpanded: false,
      links: [
        { to: '/compliance/complaints-register', icon: ClipboardCheck, label: 'Complaints', module: 'Auditor', permission: 'auditor_access.view' },
        { to: '/compliance/conflict-of-interest-register', icon: ClipboardCheck, label: 'Conflict of Interest', module: 'Auditor', permission: 'auditor_access.view' },
        { to: '/compliance/risk-register', icon: ClipboardCheck, label: 'Risk Register', module: 'Auditor', permission: 'auditor_access.view' },
        { to: '/compliance/internal-audits', icon: ClipboardCheck, label: 'Internal Audits', module: 'Auditor', permission: 'auditor_access.view' },
        { to: '/compliance/rp-register', icon: ClipboardCheck, label: 'RP Register', module: 'Incidents', permission: 'incident.report.view_all' },
        { to: '/compliance/injury-register', icon: ClipboardCheck, label: 'Injury register', module: 'Incidents', permission: 'incident.report.view_all' },
        { to: '/compliance/improvement-register', icon: ClipboardCheck, label: 'Improvement Register', module: 'Incidents', permission: 'incident.report.view_all' },
      ]
    },
    {
      section: true,
      label: 'Workforce Compliance',
      defaultExpanded: false,
      links: [
        { to: '/company/compliance', icon: ClipboardCheck, label: 'Compliance Dashboard', module: 'HRM', permission: 'compliance.dashboard.view' },
        { to: '/company/compliance/employees', icon: UserCheck, label: 'Employee Docs', module: 'HRM', permission: 'compliance.documents.view_all' },
        { to: '/company/compliance/cpd-register', icon: GraduationCap, label: 'Uploaded Certificates', module: 'HRM', permission: 'compliance.documents.view_all' },
        { to: '/company/compliance/screening-register', icon: ShieldCheck, label: 'Screening Register', module: 'HRM', permission: 'compliance.documents.view_all' },
        { to: '/company/compliance/competency-matrix', icon: Award, label: 'Competency Matrix (Docs + Courses)', module: 'HRM', permission: 'compliance.documents.view_all' },
        { to: '/company/compliance/sil-whs', icon: Home, label: 'SIL WHS', module: 'HRM', permission: 'compliance.documents.view_all' },
      ]
    },
    {
      section: true,
      label: 'Policy',
      defaultExpanded: false,
      module: 'Policy',
      links: [
        { to: '/policy', icon: FileText, label: 'Manage Policies', module: 'Policy', permission: 'policy.view' },
      ]
    },
    {
      section: true,
      label: 'Learning',
      defaultExpanded: false,
      module: 'Learning',
      links: [
        { to: '/company/learning/courses', icon: GraduationCap, label: 'Courses', module: 'Learning', permission: 'learning.courses.view' },
        { to: '/company/learning/completion-reviews', icon: ClipboardCheck, label: 'Completion Reviews', module: 'Learning', permission: 'learning.enrollments.manage' },
        { to: '/company-settings/learning', icon: Settings, label: 'Learning Settings', module: 'Learning', permission: 'learning.courses.manage' },
      ]
    },
    {
      section: true,
      label: 'Recruitment',
      defaultExpanded: false,
      module: 'Recruitment',
      links: [
        { to: '/recruitment', icon: Briefcase, label: 'Dashboard', module: 'Recruitment', permission: 'recruitment.dashboard.view' },
        { to: '/recruitment/jobs', icon: FileText, label: 'Jobs', module: 'Recruitment', permission: 'recruitment.jobs.view' },
        { to: '/recruitment/interviews', icon: Calendar, label: 'Interviews', module: 'Recruitment', permission: 'recruitment.interviews.view' },
        { to: '/recruitment/offers', icon: FileSignature, label: 'Offers', module: 'Recruitment', permissions: ['recruitment.offers.view', 'recruitment.offers.read'] },
        { to: '/recruitment/onboarding-dashboard', icon: ClipboardList, label: 'Onboarding', module: 'Recruitment', permission: 'recruitment.onboarding.view' },
        { to: '/recruitment/reference-check', icon: UserCheck, label: 'References', modules: ['Referral', 'Recruitment'], permission: 'recruitment.reference_checks.view' },
        { to: '/recruitment/interview-kits', icon: PenTool, label: 'Interview Kits', module: 'Recruitment', permission: 'recruitment.interview_kits.manage' }
      ]
    },
    { 
      section: true, 
      label: 'Finance & Payroll',
      defaultExpanded: false,
      links: [
        { to: '/invoices', icon: FileText, label: 'Invoices', module: 'Invoicing', permission: 'billing.invoice.view' },
        { to: '/payroll/dashboard', icon: Calculator, label: 'Payroll', module: 'Payroll', permission: 'payroll.view' },
        { to: '/ndis/award-rules', icon: DollarSign, label: 'Award Rates', module: 'HRM', permission: 'hr.award_rules.view' },
      ]
    },
    {
      section: true,
      label: 'Documents',
      defaultExpanded: false,
      links: [
        { to: '/document-templates', icon: FileText, label: 'Templates', module: 'Documents', permission: 'documents.templates.view' },
        { to: '/generated-documents', icon: FileCheck, label: 'Generated', module: 'Documents', permission: 'documents.templates.view' },
        { to: '/esignature', icon: PenTool, label: 'E-Signatures', module: 'E-Sign', permission: 'documents.esignature.view' }
      ]
    },
    { 
      section: true, 
      label: 'Reports',
      defaultExpanded: false,
      module: 'Reports',
      links: [
        { to: '/reports', icon: TrendingUp, label: 'All Reports', module: 'Reports', permission: 'reports.view' }
      ]
    },
    {
      section: true,
      label: 'Admin',
      defaultExpanded: false,
      links: [
        { 
          to: '/company-settings', 
          icon: Settings, 
          label: 'Company Settings', 
          dataTour: 'settings-nav',
          permissions: [
            'settings.company.view',
            'scheduling.shift.view_all',
            'quotes.view',
            'quotes.service_agreement.create',
            'settings.notification_templates.view',
            'incident.report.view_all',
            'compliance.dashboard.view',
            'hr.award_rules.view',
            'settings.roles.manage',
            'recruitment.settings.view',
            'hr.employee.view_all',
            'sleepover.settings.view',
            'payroll.view',
            'auditor_access.view'
          ]
        },
        { to: '/roles', icon: Shield, label: 'Role Management', permission: 'settings.roles.manage' },
        { to: '/user-roles', icon: Users, label: 'User Roles', permission: 'settings.system_roles.assign' },
        { to: '/mobile-app', icon: Smartphone, label: 'Mobile App', module: 'Core', permission: 'mobile.admin.view' },
        { to: '/audit-host', icon: Headset, label: 'Audit Host', module: 'Auditor', permissions: ['audit_host.console.view', 'auditor_access.view'] },
        { to: '/billing', icon: CreditCard, label: 'Billing', module: 'Core', permission: 'billing.subscription.view' },
        { to: '/integrations/xero', icon: Link2, label: 'Xero', module: 'Integrations', permission: 'integrations.xero.view' },
      ]
    },
  ];
};

// Self-service navigation for employees
const buildSelfServiceLinks = (permissions, employmentType, isRosteredEmployee) => {
  const canViewAllIncidents = permissions?.includes('incident.report.view_all');
  
  return [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', isPublic: true },
    { 
      section: true, 
      label: 'My Profile',
      defaultExpanded: true,
      links: [
        { to: '/profile', icon: User, label: 'Overview', permission: 'profile.personal.view' },
        { to: '/profile/personal', icon: User, label: 'Personal Info', permission: 'profile.personal.view' },
        { to: '/profile/employment', icon: Briefcase, label: 'Employment', permission: 'hr.employee.view_self' },
        { to: '/profile/documents', icon: FileText, label: 'My Documents', permission: 'compliance.documents.view_own' },
        { to: '/profile/compliance', icon: FileCheck, label: 'Compliance & Training', permission: 'compliance.documents.view_own' },
        { to: '/profile/learning', icon: GraduationCap, label: 'My Learning', module: 'Learning', permission: 'learning.my_courses' },
      ]
    },
    { 
      section: true, 
      label: 'My Work',
      defaultExpanded: true,
      links: [
        ...(isRosteredEmployee ? [
          { to: '/profile/my-shifts', icon: Calendar, label: 'My Shifts', permission: 'scheduling.shift.view' }
        ] : []),
        // Availability - only for casual and contractor employees
        ...((employmentType === 'casual' || employmentType === 'contractor') && isRosteredEmployee ? [
          { to: '/availability', icon: CalendarRange, label: 'My Availability', permission: 'scheduling.shift.view' }
        ] : []),
        // Leave & Time Off - only for full-time and part-time employees
        ...((employmentType === 'full_time' || employmentType === 'part_time' || employmentType === 'fulltime' || employmentType === 'parttime') ? [
          { to: '/profile/leave-availability', icon: CalendarCheck, label: 'Leave & Time Off', permission: 'leave.request.view_own' }
        ] : []),
      ]
    },
    { 
      section: true, 
      label: 'Other',
      defaultExpanded: false,
      links: [
        { to: '/profile/policies', icon: FileText, label: 'My Policies', permission: 'policy.view_assigned' },
        { to: '/profile/my-incidents', icon: AlertTriangle, label: 'My Incidents', permission: 'incident.report.view_own' },
        ...(canViewAllIncidents ? [
          { to: '/incidents', icon: AlertTriangle, label: 'All Incidents', permission: 'incident.report.view_all' }
        ] : []),
      ]
    },
  ];
};


// ========== SCROLL PERSISTENCE ==========
// Module-level variable survives component remounts.
// Each page creates its own <DashboardLayout> instance, so refs are lost on navigation.
let _sidebarScrollTop = 0;

// ========== COMPONENT ==========

const DashboardLayoutV2Shell = ({ children, user: userProp, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const outlet = useOutlet();
  const navRef = useRef(null);
  const { branding } = useBranding();
  const { toast } = useToast();

  // On mount: restore sidebar scroll position from module-level variable.
  // Uses MutationObserver to wait for auto-expand DOM changes to settle first.
  useEffect(() => {
    const savedPos = _sidebarScrollTop;
    if (savedPos <= 0) return;

    const nav = navRef.current;
    if (!nav) return;

    let settleTimer = null;
    const restore = () => { nav.scrollTop = savedPos; };

    // Immediately attempt restore (works if no DOM mutations pending)
    restore();

    // Also observe for DOM mutations (e.g. auto-expanding active sections)
    const observer = new MutationObserver(() => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        restore();
        observer.disconnect();
      }, 80);
    });
    observer.observe(nav, { childList: true, subtree: true });

    // Fallback: restore after 350ms regardless
    const fallback = setTimeout(() => {
      restore();
      observer.disconnect();
    }, 350);

    return () => {
      clearTimeout(settleTimer);
      clearTimeout(fallback);
      observer.disconnect();
    };
  }, []); // Mount-only: restore scroll once when layout renders
  
  // Fallback to localStorage if user prop not provided
  const user = useMemo(() => {
    if (userProp) return userProp;
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, [userProp]);
  
  // Check if in impersonation mode
  const isImpersonating = localStorage.getItem('original_superadmin_token') !== null;
  
  // State
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const drawerScrollRef = useRef(0);

  const toggleMobileMenu = useCallback(() => {
    startTransition(() => setMobileMenuOpen((open) => !open));
  }, []);
  const [companySwitcherOpen, setCompanySwitcherOpen] = useState(false);
  const [alertBellReady, setAlertBellReady] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [expandedSections, setExpandedSections] = useState(() => {
    const saved = localStorage.getItem('expandedSections');
    return saved ? JSON.parse(saved) : {};
  });
  const [employmentType, setEmploymentType] = useState(null);
  
  // Role checks
  const isSuperadmin = user?.role === 'superadmin';
  const isCompanyAdmin = user?.role === 'company_admin';
  const platformAccess = usePlatformAccess(isImpersonating ? null : user);
  const ownerHint = ownerHintFromUser(user);
  const platformLinkVisible = useCallback((link) => {
    if (!link?.platformPermission) return true;
    if (!platformAccess) return ownerHint;
    return hasPlatformPermission(platformAccess, link.platformPermission);
  }, [platformAccess, ownerHint]);
  
  // Load modules
  const { modules: availableModules = [], oncallEnabled = false } = useAvailableModules();
  const hasModule = useCallback((m) => isSuperadmin || (availableModules || []).includes(m), [isSuperadmin, availableModules]);
  
  // Load permissions
  const { hasPermission, isAdmin: hasAllPermissions, permissions, loading: permissionsLoading } = usePermissions();
  const { loading: modulesLoading } = useAvailableModules();

  // Fall back to session permissions when context refresh failed or is still loading
  const effectivePermissions = useMemo(() => {
    if (permissions?.length > 0) return permissions;
    if (Array.isArray(user?.permissions) && user.permissions.length > 0) return user.permissions;
    return permissions || [];
  }, [permissions, user?.permissions]);

  const effectiveHasPermission = useCallback((permissionKey) => {
    if (hasAllPermissions || isSuperadmin || isCompanyAdmin) return true;
    return effectivePermissions.includes(permissionKey);
  }, [hasAllPermissions, isSuperadmin, isCompanyAdmin, effectivePermissions]);
  
  // Check if user only has self-service permissions
  const hasSelfServicePermissionsOnly = useMemo(() => {
    return checkSelfServiceOnly({
      permissions: effectivePermissions,
      isAdmin: hasAllPermissions,
      isCompanyAdmin,
      isSuperadmin,
      permissionsLoading,
      modulesLoading,
    });
  }, [isSuperadmin, isCompanyAdmin, hasAllPermissions, effectivePermissions, permissionsLoading, modulesLoading]);
  
  // Check if user is rostered
  const isRosteredEmployee = useMemo(() => {
    if (isSuperadmin || isCompanyAdmin) return false;
    if (!effectivePermissions || effectivePermissions.length === 0) return false;
    
    const rosterPermissions = ['scheduling.shift.view', 'mobile.shifts.view', 'mobile.clock.access'];
    const scPermissions = ['sc.dashboard.view', 'sc.tasks.view', 'sc.case_notes.view'];
    
    const hasRosterPerms = rosterPermissions.some(p => effectivePermissions.includes(p));
    const hasSCPerms = scPermissions.some(p => effectivePermissions.includes(p));
    
    if (hasSCPerms && !hasRosterPerms) return false;
    return hasRosterPerms;
  }, [isSuperadmin, isCompanyAdmin, effectivePermissions]);
  
  // Check if user has access to any settings (for showing/hiding settings icon)
  // Canonical list of all permissions that grant access to at least one settings page
  // This must stay in sync with: App.js /company-settings route AND CompanySettingsPage.js SETTINGS_PERMISSIONS
  const SETTINGS_PERMISSIONS = [
    'settings.company.view',
    'scheduling.shift.view_all',
    'quotes.view',
    'quotes.service_agreement.create',
    'settings.notification_templates.view',
    'incident.report.view_all',
    'compliance.dashboard.view',
    'hr.award_rules.view',
    'settings.roles.manage',
    'recruitment.settings.view',
    'hr.employee.view_all',
    'sleepover.settings.view',
    'payroll.view',
    'auditor_access.view',
    'sil.house.view',
    'sil.house.manage',
    'learning.courses.manage',
    'help.settings.manage',
  ];
  
  const hasAnySettingsAccess = useMemo(() => {
    if (isSuperadmin || isCompanyAdmin || hasAllPermissions) return true;
    return SETTINGS_PERMISSIONS.some(p => effectivePermissions.includes(p));
  }, [isSuperadmin, isCompanyAdmin, hasAllPermissions, effectivePermissions]);
  
  // Billing-only mode check - company admin with suspended company
  // Updated to use read_only_mode instead of billing_only_mode
  const isReadOnly = isReadOnlyMode(user);
  
  // In read-only mode, allow ALL navigation but actions are blocked
  // Billing page has full functional access
  
  // Access check helper - Module gating applies to ALL users except superadmin
  const canAccessNavItem = useCallback((item) => {
    // Superadmin bypasses all checks
    if (isSuperadmin) return true;
    
    // READ-ONLY MODE: Allow ALL navigation (but actions blocked at API level)
    // This replaces the old billing_only_mode which restricted to billing page only
    // Now users can view all their data in suspended/pending_deletion status
    
    // Hide if user HAS a specific module (for showing simplified alternatives)
    if (item.hideIfModule && hasModule(item.hideIfModule)) {
      return false;
    }
    
    // Module check applies to everyone (including company_admin)
    // Support both single module and array of modules
    if (item.modules && Array.isArray(item.modules)) {
      // If modules array provided, user needs ANY of them
      if (!item.modules.some(m => hasModule(m))) return false;
    } else if (item.module && !hasModule(item.module)) {
      return false;
    }
    
    // Company-level feature flag gating (e.g. On-Call master toggle). Hidden when off.
    if (item.companyFlag === 'oncallEnabled' && !oncallEnabled) {
      return false;
    }
    
    // Permission checks - company_admin has all permissions
    if (isCompanyAdmin || hasAllPermissions) return true;
    
    if (!item.permission && !item.permissions) return item.isPublic === true;
    
    // If item has permissions array, check if user has ANY of them
    if (item.permissions && Array.isArray(item.permissions)) {
      return item.permissions.some(p => effectiveHasPermission(p));
    }
    
    // Single permission check
    return effectiveHasPermission(item.permission);
  }, [isSuperadmin, isCompanyAdmin, hasAllPermissions, hasModule, effectiveHasPermission, oncallEnabled]);

  // Shared with Profile Messages — group OR shift/on-call messaging surface
  const { canAccessMessages } = useProfileHubAccess();

  const canSwitchCompany = useMemo(
    () =>
      !isSuperadmin &&
      Array.isArray(user?.company_ids) &&
      user.company_ids.length > 1,
    [isSuperadmin, user?.company_ids]
  );
  
  // READ-ONLY MODE: No redirect needed - users can navigate everywhere
  // The read-only banner shows and API calls for write operations return 403
  
  // Persist sidebar state
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);
  
  // Persist expanded sections (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      localStorage.setItem('expandedSections', JSON.stringify(expandedSections));
    }, 300);
    return () => clearTimeout(t);
  }, [expandedSections]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Body scroll lock + Escape when mobile drawer open
  useEffect(() => {
    if (!isMobile || !mobileMenuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [isMobile, mobileMenuOpen]);

  // Restore drawer scroll after remount
  useEffect(() => {
    if (!mobileMenuOpen || !isMobile) return;
    const nav = navRef.current;
    if (nav && drawerScrollRef.current) {
      nav.scrollTop = drawerScrollRef.current;
    }
  }, [mobileMenuOpen, isMobile]);

  // Superadmin has no company context; the alerts API 401s and the session interceptor logs them out.
  const showAlertBell = !(isSuperadmin && !isImpersonating);

  // Defer AlertBell until idle so shell paint is not blocked by poll setup
  useEffect(() => {
    if (!showAlertBell) {
      setAlertBellReady(false);
      return undefined;
    }
    let idleId;
    let timeoutId;
    const enable = () => setAlertBellReady(true);
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(enable, { timeout: 2500 });
    } else {
      timeoutId = setTimeout(enable, 1500);
    }
    return () => {
      if (idleId != null && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [showAlertBell]);
  
  // Employment type from session bootstrap (user.employee) — no full employee list fetch
  useEffect(() => {
    if (user?.employee?.employment_type) {
      setEmploymentType(user.employee.employment_type);
    }
  }, [user?.employee?.employment_type]);
  
  // Build navigation links based on user type
  const navigationLinks = useMemo(() => {
    if (isSuperadmin && !isImpersonating) {
      return SUPERADMIN_LINKS.map((item) => {
        if (item.section && item.links) {
          const links = item.links.filter((link) => platformLinkVisible(link));
          if (!links.length) return null;
          return { ...item, links };
        }
        return platformLinkVisible(item) ? item : null;
      }).filter(Boolean);
    }
    if (hasSelfServicePermissionsOnly && user?.role === 'employee') {
      return buildSelfServiceLinks(effectivePermissions, employmentType, isRosteredEmployee);
    }
    return buildCompanyAdminLinks(availableModules || []);
  }, [isSuperadmin, isImpersonating, platformLinkVisible, hasSelfServicePermissionsOnly, user?.role, effectivePermissions, employmentType, isRosteredEmployee, availableModules]);

  const taskRedirectRef = useRef('');
  useEffect(() => {
    if (!isSuperadmin || isImpersonating) return;
    if (!platformAccess && !ownerHint) return;
    const access = platformAccess || { is_platform_owner: true, all_permissions: true, permissions: [] };
    if (pathAllowed(location.pathname, access)) {
      taskRedirectRef.current = '';
      return;
    }
    if (taskRedirectRef.current === location.pathname) return;
    taskRedirectRef.current = location.pathname;
    toast({
      title: 'That area is not in your tasks',
      description: 'Ask the platform owner if you need access.',
    });
    navigate('/dashboard', { replace: true });
  }, [isSuperadmin, isImpersonating, platformAccess, ownerHint, location.pathname, navigate, toast]);

  const visibleNavigation = useMemo(() => {
    return navigationLinks
      .map((item) => {
        if (!item.section) {
          return canAccessNavItem(item) ? item : null;
        }
        if (item.modules && Array.isArray(item.modules) && !item.modules.some((m) => hasModule(m))) {
          return null;
        }
        if (item.module && !hasModule(item.module)) {
          return null;
        }
        const accessibleLinks = item.links.filter((link) => canAccessNavItem(link));
        if (accessibleLinks.length === 0) return null;
        return { ...item, links: accessibleLinks };
      })
      .filter(Boolean);
  }, [navigationLinks, canAccessNavItem, hasModule]);
  
  const toggleSection = (sectionLabel) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionLabel]: !prev[sectionLabel]
    }));
  };
  
  // Check if path is active
  const isLinkActive = (linkPath, link = {}) => {
    const currentPath = location.pathname;

    if (link.activePaths?.length) {
      if (link.activePaths.some((p) => currentPath === p || currentPath.startsWith(`${p}/`))) {
        return true;
      }
    }
    
    // Handle links with query strings (e.g. /assistant?tab=ai-management).
    // These need both pathname AND tab query param to match.
    if (linkPath.includes('?')) {
      const [linkPathOnly, linkQuery] = linkPath.split('?');
      if (currentPath !== linkPathOnly) return false;
      const linkParams = new URLSearchParams(linkQuery);
      const currentParams = new URLSearchParams(location.search);
      for (const [k, v] of linkParams.entries()) {
        if (currentParams.get(k) !== v) return false;
      }
      return true;
    }
    
    // Plain /assistant link should NOT light up when a tab query is active
    // (so AI Mgmt/Training/Templates tabs claim the active state instead).
    if (linkPath === '/assistant' && currentPath === '/assistant' && location.search.includes('tab=')) {
      return false;
    }
    
    if (currentPath === linkPath) return true;
    
    const exactMatchOnlyPaths = [
      '/policy', '/recruitment', '/ndis', '/company/compliance',
      '/esignature', '/document-templates', '/support-coordination'
    ];
    
    if (exactMatchOnlyPaths.includes(linkPath)) return false;
    
    if (currentPath.startsWith(linkPath + '/')) {
      const remainder = currentPath.substring(linkPath.length + 1);
      const isLikelyDynamicId = /^[a-f0-9-]{36}$/.test(remainder.split('/')[0]) || 
                                /^\d+$/.test(remainder.split('/')[0]) ||
                                remainder === 'new' || 
                                remainder === 'edit' ||
                                remainder.startsWith('new/') ||
                                remainder.startsWith('edit/');
      return isLikelyDynamicId;
    }
    
    return false;
  };
  
  const isSectionActive = (links) => links.some(link => isLinkActive(link.to, link));
  
  // Auto-expand active sections
  useEffect(() => {
    let hasUpdates = false;
    const updates = {};
    visibleNavigation.forEach(item => {
      if (item.section && isSectionActive(item.links) && !expandedSections[item.label]) {
        updates[item.label] = true;
        hasUpdates = true;
      }
    });
    if (hasUpdates) {
      setExpandedSections(prev => ({ ...prev, ...updates }));
    }
  }, [location.pathname, location.search]); // eslint-disable-line
  
  // Company name comes from session bootstrap on user object
  useEffect(() => {
    if (user?.company_name) {
      setCompanyName(user.company_name);
    }
  }, [user?.company_name]);

  // User display info
  const userName = user?.first_name 
    ? `${user.first_name} ${user.last_name || ''}`.trim()
    : user?.email?.split('@')[0] || 'User';
  
  const userInitials = user?.first_name
    ? `${user.first_name[0]}${user.last_name?.[0] || ''}`
    : user?.email?.[0]?.toUpperCase() || 'U';
  
  // Render navigation link - V2 Purple Theme
  const renderNavLink = (link, isNested = false) => {
    if (!canAccessNavItem(link)) return null;
    
    const Icon = link.icon;
    const isActive = isLinkActive(link.to, link);
    const isLocked = link.locked && !isSuperadmin;
    
    // Mobile single-panel drawer (larger tap targets)
    if (isMobile && !sidebarCollapsed) {
      return (
        <Link
          key={link.to}
          to={isLocked ? '#' : link.to}
          data-tour={link.dataTour}
          onClick={(e) => {
            if (isLocked) e.preventDefault();
            else setMobileMenuOpen(false);
          }}
          className={`flex items-center gap-4 rounded-xl transition-colors ${
            isActive
              ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-medium'
              : isLocked
              ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
          } ${isNested ? 'px-4 py-3 ml-4' : 'px-4 py-4'}`}
        >
          <Icon size={22} className={`flex-shrink-0 ${isActive ? 'text-purple-600 dark:text-purple-400' : ''}`} />
          <span className="truncate text-base flex items-center gap-2">
            {link.label}
            {isLocked && <Lock size={14} />}
          </span>
        </Link>
      );
    }
    
    if (sidebarCollapsed) {
      return (
        <Link
          key={link.to}
          to={isLocked ? '#' : link.to}
          data-tour={link.dataTour}
          onClick={(e) => {
            if (isLocked) e.preventDefault();
            else setMobileMenuOpen(false);
          }}
          className={`flex items-center justify-center p-2 rounded-lg transition-colors ${
            isActive
              ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
              : isLocked
              ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
              : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
          }`}
          title={link.label}
        >
          <Icon size={18} className="flex-shrink-0" />
        </Link>
      );
    }
    
    return (
      <Link
        key={link.to}
        to={isLocked ? '#' : link.to}
        data-tour={link.dataTour}
        onClick={(e) => {
          if (isLocked) e.preventDefault();
          else setMobileMenuOpen(false);
        }}
        className={`flex items-center gap-3 rounded-lg transition-colors text-sm ${
          isActive
            ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-medium'
            : isLocked
            ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
        } ${isNested ? 'px-3 py-1.5 ml-4' : 'px-3 py-2'}`}
      >
        <Icon size={16} className={`flex-shrink-0 ${isActive ? 'text-purple-600 dark:text-purple-400' : isLocked ? '' : 'text-gray-400 dark:text-gray-500'}`} />
        <span className="truncate flex items-center gap-1">
          {link.label}
          {isLocked && <Lock size={12} />}
        </span>
      </Link>
    );
  };
  
  // Render section - accordion on mobile and desktop (single panel)
  const renderSection = (section, idx) => {
    const accessibleLinks = section.links;
    const sectionLocked = section.locked && !isSuperadmin;
    const isExpanded = expandedSections[section.label] ?? section.defaultExpanded ?? false;
    const hasActiveLink = isSectionActive(accessibleLinks);
    
    // Mobile: accordion in one drawer (not a second sliding panel)
    if (isMobile && !sidebarCollapsed) {
      const SectionIcon = section.links[0]?.icon || Folder;
      return (
        <div key={`section-${idx}`} className="mb-1">
          <button
            type="button"
            onClick={() => toggleSection(section.label)}
            className={`w-full flex items-center justify-between px-4 py-4 rounded-xl transition-colors ${
              sectionLocked 
                ? 'text-gray-300 dark:text-gray-600' 
                : hasActiveLink 
                ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300' 
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            <div className="flex items-center gap-4">
              <SectionIcon size={22} className={`${hasActiveLink ? 'text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'}`} />
              <span className="text-base font-medium flex items-center gap-2">
                {section.label}
                {sectionLocked && <Lock size={14} />}
              </span>
            </div>
            {isExpanded ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
          </button>
          {isExpanded && (
            <div className="mt-1 space-y-0.5">
              {accessibleLinks.map(link => renderNavLink(link, true))}
            </div>
          )}
        </div>
      );
    }
    
    if (sidebarCollapsed) {
      return (
        <div key={`section-${idx}`} className="py-1 border-t border-gray-100 dark:border-gray-800 first:border-t-0">
          {accessibleLinks.map(link => renderNavLink(link, false))}
        </div>
      );
    }
    
    return (
      <div key={`section-${idx}`} className="mb-1">
        {(() => {
          const SectionIcon = section.links[0]?.icon;
          return (
            <button
              onClick={() => toggleSection(section.label)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                sectionLocked 
                  ? 'text-gray-300 dark:text-gray-600' 
                  : hasActiveLink 
                  ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300' 
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              <div className="flex items-center gap-3">
                {SectionIcon && (
                  <SectionIcon size={16} className={`${hasActiveLink ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400 dark:text-gray-500'}`} />
                )}
                <span className="text-sm font-medium flex items-center gap-1">
                  {section.label}
                  {sectionLocked && <Lock size={12} />}
                </span>
              </div>
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          );
        })()}
        
        {isExpanded && (
          <div className="mt-1 space-y-0.5">
            {accessibleLinks.map(link => renderNavLink(link, true))}
          </div>
        )}
      </div>
    );
  };
  
  // Save scroll position to module-level var before any link click triggers navigation
  const saveScrollPos = useCallback(() => {
    const nav = navRef.current;
    if (nav) {
      _sidebarScrollTop = nav.scrollTop;
      drawerScrollRef.current = nav.scrollTop;
    }
  }, []);

  // Render navigation
  const renderNavigation = () => (
    <nav 
      ref={navRef}
      onClickCapture={saveScrollPos}
      onScroll={(e) => {
        drawerScrollRef.current = e.currentTarget.scrollTop;
      }}
      className="flex-1 overflow-y-auto thin-scrollbar p-3 space-y-1 pb-4"
    >
      {visibleNavigation.map((item, idx) => {
        if (item.section) return renderSection(item, idx);
        return renderNavLink(item, false);
      })}
    </nav>
  );
  
  // User section with 5 icons at bottom - V2 Style
  const renderUserSection = () => (
    <div className={`flex-shrink-0 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 ${sidebarCollapsed ? 'p-2' : 'p-3'}`}>
      {!sidebarCollapsed && (
        <div className="mb-3">
          {companyName && (
            <p className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 truncate mb-2 tracking-wide uppercase">{companyName}</p>
          )}
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 text-sm">
                {userInitials}
              </AvatarFallback>
            </Avatar>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{userName}</p>
          </div>
        </div>
      )}
      
      {/* Bottom Icons - All visible in read-only mode (users can navigate everywhere) */}
      <div className={`flex items-center ${sidebarCollapsed ? 'flex-col gap-1' : 'justify-between'}`}>
        {/* Settings */}
        {((isSuperadmin && hasPlatformPermission(
          platformAccess || (ownerHint ? { is_platform_owner: true, all_permissions: true, permissions: [] } : null),
          'owner',
        )) || (!isSuperadmin && hasAnySettingsAccess)) && (
          <Link 
            to={isSuperadmin ? "/settings" : "/company-settings"} 
            className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100 rounded-lg transition-colors" 
            title="Settings"
          >
            <Settings size={18} />
          </Link>
        )}
        {/* Profile */}
        <Link 
          to="/profile" 
          className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100 rounded-lg transition-colors" 
          title="Profile"
        >
          <User size={18} />
        </Link>
        {canAccessMessages && !isSuperadmin && (
          <Link
            to="/messaging"
            className={`p-2 rounded-lg transition-colors ${
              location.pathname.startsWith('/messaging')
                ? 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
            title="Messages"
            data-testid="sidebar-messages-btn"
          >
            <MessageSquare size={18} />
          </Link>
        )}
        {showAlertBell && alertBellReady && (
          <Suspense fallback={null}>
            <AlertBellLazy />
          </Suspense>
        )}
        {/* Company Switcher — only when user belongs to more than one company */}
        {canSwitchCompany && (
          <button 
            onClick={() => setCompanySwitcherOpen(true)} 
            className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors" 
            title="Switch Company"
          >
            <RefreshCw size={18} />
          </button>
        )}
        {/* Help */}
        <button 
          onClick={() => setHelpOpen(true)} 
          className="p-2 text-gray-500 dark:text-gray-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:text-purple-600 dark:hover:text-purple-400 rounded-lg transition-colors" 
          title="Help & Resources"
          data-testid="sidebar-help-btn"
        >
          <HelpCircle size={18} />
        </button>
        <button 
          onClick={onLogout} 
          className="p-2 text-gray-500 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400 rounded-lg transition-colors" 
          title="Logout"
        >
          <LogOut size={18} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Inject scrollbar styles */}
      <style>{scrollbarStyles}</style>
      
      {/* Impersonation Banner */}
      <ImpersonationBanner />
      
      {/* Mobile header */}
      <div className={`lg:hidden fixed top-0 left-0 right-0 h-20 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 z-50 flex items-center justify-between px-4 ${isImpersonating ? 'mt-12' : ''}`}>
        <button
          onClick={toggleMobileMenu}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 active:scale-95 transition-transform"
        >
          {mobileMenuOpen ? <X className="h-8 w-8 text-gray-600 dark:text-gray-300" /> : <Menu className="h-8 w-8 text-gray-600 dark:text-gray-300" />}
        </button>
        <BrandLogo variant="auto" size="sm" />
        <Avatar className="h-10 w-10">
          <AvatarFallback className="bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 text-sm">
            {userInitials}
          </AvatarFallback>
        </Avatar>
      </div>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden transition-opacity duration-300"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar — on mobile only mount when open (no offscreen full nav tree) */}
      {(!isMobile || mobileMenuOpen) && (
      <aside className={`
        fixed top-0 left-0 h-full w-[80%] lg:w-auto bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 z-40
        transition-transform duration-300 ease-out
        ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'}
        ${isMobile ? (mobileMenuOpen ? 'translate-x-0' : '-translate-x-full') : 'translate-x-0'}
        ${isImpersonating ? 'pt-12' : ''}
      `}>
        <div className={`h-full flex flex-col relative ${!sidebarCollapsed ? 'pt-20 lg:pt-0' : ''}`}>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="absolute -right-3 top-20 z-50 w-6 h-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full shadow-sm flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 hidden lg:flex"
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? <ChevronRight size={14} className="text-gray-500" /> : <ChevronLeft size={14} className="text-gray-500" />}
          </button>
          
          <div className={`h-16 lg:flex items-center border-b border-gray-200 dark:border-gray-700 hidden ${sidebarCollapsed ? 'justify-center px-2' : 'px-3'}`}>
            {!sidebarCollapsed ? (
              <BrandLogo variant="auto" size="sm" className="w-full justify-center" imgClassName="!h-12 !max-w-[216px]" />
            ) : (
              <BrandIcon size="md" />
            )}
          </div>
          
          {renderNavigation()}
          {renderUserSection()}
        </div>
      </aside>
      )}

      {/* Document title only — no SEO network fetches in authenticated shell */}
      <Helmet>
        <title>{`${branding?.site_name || 'Provisoft'} | ${location.pathname.split('/').filter(Boolean).pop()?.replace(/-/g, ' ') || 'Dashboard'}`}</title>
      </Helmet>

      {/* Main content */}
      <main className={`
        lg:transition-[margin] lg:duration-300
        ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}
        ${isImpersonating ? 'pt-32 lg:pt-12' : 'pt-20 lg:pt-0'}
      `}>
        <ComplianceWarningBanner user={user} />
        <ReadOnlyBanner user={user} />
        <div className="p-4 lg:p-6">
          {outlet || children}
        </div>
      </main>

      <ContextualHelpWidget triggerOpen={helpOpen} onOpenChange={setHelpOpen} />

      <Suspense fallback={null}>
        <CopilotBubbleLazy />
      </Suspense>
      
      {companySwitcherOpen && (
        <Suspense fallback={null}>
          <CompanySwitcherModalLazy
            isOpen={companySwitcherOpen}
            onClose={() => setCompanySwitcherOpen(false)}
            currentCompanyId={user?.company_id}
            onSwitch={(data) => {
              toast({
                title: 'Company Switched',
                description: `Now viewing ${data.company_name}`
              });
            }}
          />
        </Suspense>
      )}
    </div>
  );
};

/** Skip nested shell when AuthenticatedShell already provides layout (C1). */
const DashboardLayoutV2 = (props) => {
  const inShell = useInDashboardShell();
  if (inShell) {
    return props.children;
  }
  return <DashboardLayoutV2Shell {...props} />;
};

export { DashboardLayoutV2Shell };
export default DashboardLayoutV2;
